/**
 * Helpers that gate the calendar make-up flow.
 *
 * Make-up is the ability to finish a missed past day's quests later in
 * the same Mon-Fri school week. The rules:
 *
 *   - The make-up window starts at the Monday of THIS calendar week.
 *     Days before that are never eligible (we don't reopen prior weeks).
 *   - The window further clamps to the user's `firstActiveDate`: a
 *     brand-new account that just installed today can't make up a
 *     Monday it never actually saw.
 *   - The target date must be strictly in the past (today's quests are
 *     the active set; today is not "made up").
 *   - The day must not already be a completed training day.
 *   - We must have an archived quest slice for that day — if rollover
 *     never ran for it (e.g. the app wasn't opened that day) we have
 *     nothing concrete to credit a make-up against, so the calendar
 *     shows the day as untappable.
 */
import { formatDateISO, isSchoolDay, weekStartISO } from './dateUtils.js';

/**
 * Earliest ISO date that's a candidate for make-up TODAY for this user.
 * Returns null when no past day is eligible at all (new account).
 *
 * The rule is intentionally simple: anyone with at least one committed
 * session can make up any school day inside THIS Mon-Fri week. The
 * previously-considered `firstActiveDate` gate turned out to over-
 * restrict — for users whose first commit lands later in the week it
 * gated them out of making up the earlier days, which contradicts the
 * spec ("παλιός παίκτης μπορεί να αναπληρώσει αυτή την εβδομάδα").
 */
export function getMakeupEarliestISO(state, now = new Date()) {
  if (!state) return null;
  // Brand-new accounts get no past-day make-up — there's no past for them.
  if ((state.totalSessions ?? 0) === 0) return null;
  return weekStartISO(now);
}

/**
 * True when `dateISO` is a tap-to-make-up target right now.
 *
 * Make-up only applies to school days (Mon-Fri) inside the eligibility
 * window. Weekend cells inside the window are inert even when the
 * window technically includes them (e.g. opening the app on a Sunday).
 *
 * The archive is populated lazily by `ensureDailyQuests` on home-page
 * mount, so by the time this is consulted the archive entry exists for
 * any eligible school day. We still check for it as a defensive guard.
 */
export function isMakeupEligible(state, dateISO, now = new Date()) {
  if (!state || typeof dateISO !== 'string') return false;
  if (!isSchoolDay(dateISO)) return false;
  const todayISO = formatDateISO(now);
  if (dateISO >= todayISO) return false;
  const earliest = getMakeupEarliestISO(state, now);
  if (!earliest) return false;
  if (dateISO < earliest) return false;
  if (
    Array.isArray(state.completedTrainingDays) &&
    state.completedTrainingDays.includes(dateISO)
  ) {
    return false;
  }
  if (
    !state.dailyQuestHistory ||
    typeof state.dailyQuestHistory !== 'object' ||
    !state.dailyQuestHistory[dateISO]
  ) {
    return false;
  }
  return true;
}

/**
 * List every ISO school day inside the eligibility window — used by
 * `ensureDailyQuests` to know which past days to lazily synthesize
 * into the archive. Returned dates are oldest-first.
 */
export function listMakeupSchoolDays(state, now = new Date()) {
  const earliest = getMakeupEarliestISO(state, now);
  if (!earliest) return [];
  const todayISO = formatDateISO(now);
  const out = [];
  const cursor = new Date(`${earliest}T00:00:00`);
  const end = new Date(`${todayISO}T00:00:00`);
  while (cursor < end) {
    if (isSchoolDay(cursor)) {
      out.push(formatDateISO(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/**
 * Convenience for the calendar UI — returns the list of ISO dates inside
 * the visible range that the child can tap to make up. Ordered oldest-first.
 */
export function listMakeupCandidates(state, now = new Date()) {
  const earliest = getMakeupEarliestISO(state, now);
  if (!earliest) return [];
  const todayISO = formatDateISO(now);
  const archive = state?.dailyQuestHistory ?? {};
  return Object.keys(archive)
    .filter((iso) => iso >= earliest && iso < todayISO)
    .filter((iso) => !(state.completedTrainingDays ?? []).includes(iso))
    .sort();
}
